import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import TabelaOrdensConta from '../components/TabelaOrdensConta';
import { infoSpyPjService } from '../services/api';
import '../styles/InfoSpyPj.css';

const POLL_MS = 5000;

const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

const isEmptyResult = (v) => {
  if (v === null || v === undefined || v === '') return true;
  const s = String(v).trim().toUpperCase();
  return s === 'SEM RESULTADO' || s === 'N/A' || s === '-';
};

/** Padrão Siebel: 8-A8639A70FC6-1. Ignora texto extra colado junto. */
const ORDEM_SIEBEL_RE = /\d+-[A-Z0-9]+-\d+/;

const extrairOrdemSiebel = (raw) => {
  const compact = String(raw || '').toUpperCase().replace(/\s+/g, '');
  const match = compact.match(ORDEM_SIEBEL_RE);
  return match ? match[0] : '';
};

const formatCnpjInput = (value) => {
  const n = value.replace(/\D/g, '').slice(0, 14);
  return n
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

const formatCnpjDisplay = (cnpj) => {
  const n = String(cnpj || '').replace(/\D/g, '');
  if (n.length === 11) {
    return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (n.length !== 14) return cnpj || '—';
  return n.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

const formatCpfDisplay = (cpf) => {
  const n = String(cpf || '').replace(/\D/g, '');
  if (n.length !== 11) return cpf || '—';
  return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatDateBr = (iso) => {
  if (!iso) return '—';
  if (/^\d{2}\/\d{2}\/\d{4}/.test(iso)) return iso;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR');
};

const formatPhone = (phone) => {
  if (!phone) return '—';
  if (typeof phone === 'string') {
    const n = phone.replace(/\D/g, '');
    if (n.length === 10) return n.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    if (n.length === 11) return n.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    return phone;
  }
  const full = `${phone.area || ''}${phone.number || ''}`;
  return formatPhone(full);
};

const formatCep = (cep) => {
  const n = String(cep || '').replace(/\D/g, '');
  if (n.length !== 8) return cep || '—';
  return n.replace(/(\d{5})(\d{3})/, '$1-$2');
};

const formatMoney = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const num = Number(v);
  if (Number.isNaN(num)) return String(v);
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

function Campo({ label, valor }) {
  return (
    <div className="ispjp-field">
      <label>{label}</label>
      <span>{fmt(valor)}</span>
    </div>
  );
}

function buildAddress(addr) {
  if (!addr) return '—';
  const parts = [
    [addr.street, addr.number].filter(Boolean).join(', '),
    addr.details,
    addr.district,
    [addr.city, addr.state].filter(Boolean).join(' - '),
    addr.zip ? `CEP ${formatCep(addr.zip)}` : null
  ].filter(Boolean);
  return parts.join(' · ') || '—';
}

const statusConfig = (status) => {
  const map = {
    pending: { label: 'Na fila', class: 'ispjp-status-pending' },
    processing: { label: 'Processando', class: 'ispjp-status-processing' },
    completed: { label: 'Concluído', class: 'ispjp-status-completed' },
    error: { label: 'Erro', class: 'ispjp-status-error' }
  };
  return map[status] || map.pending;
};

function ResultadoSmartConsultas({ resultado, consulta }) {
  if (!resultado) return null;
  const conta = resultado.conta || {};
  const ordem = resultado.ordem || {};
  const campos = ordem.campos || {};
  const itens = resultado.itens || [];
  const atividades = resultado.atividades || [];
  const ordemAberta = Boolean(String(ordem.numero || '').trim());

  return (
    <>
      <div className="ispjp-section-title">Conta</div>
      <div className="ispjp-field-grid">
        <Campo label="Nome" valor={conta.nome} />
        <Campo label="Documento" valor={formatCnpjDisplay(conta.documento)} />
        <Campo label="Endereço" valor={conta.endereco} />
        <Campo label="Tel. celular" valor={conta.telefoneCel} />
        <Campo label="Tel. residencial" valor={conta.telefoneRes} />
        <Campo label="Tel. comercial" valor={conta.telefoneCom} />
        <Campo label="E-mail" valor={conta.email} />
      </div>

      <TabelaOrdensConta ordens={resultado.ordens} ordemPedida={consulta?.ordem} />

      {ordemAberta && (
        <>
          <div className="ispjp-section-title">Ordem</div>
          <div className="ispjp-field-grid">
            <Campo label="Número" valor={ordem.numero} />
            <Campo label="Status" valor={ordem.status} />
            <Campo label="Tipo" valor={ordem.tipo} />
            <Campo label="Pacote" valor={resultado.pacote} />
          </div>
          {Object.keys(campos).length > 0 && (
            <div className="ispjp-field-grid" style={{ marginTop: '0.75rem' }}>
              {Object.entries(campos).map(([k, v]) => (
                <Campo key={k} label={k} valor={v} />
              ))}
            </div>
          )}
        </>
      )}

      {itens.length > 0 && (
        <>
          <div className="ispjp-section-title">Itens / pacote ({itens.length})</div>
          <div className="ispjp-table-wrap">
            <table className="ispjp-table">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>Nível</th>
                  <th>Preço</th>
                  <th>Promoção</th>
                  <th>Status</th>
                  <th>Tecnologia</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item, i) => (
                  <tr key={i}>
                    <td>
                      <strong>{item.produto || '—'}</strong>
                      {item.pai ? (
                        <div style={{ color: 'var(--texto-secundario)', fontSize: '0.8rem' }}>
                          Pai: {item.pai}
                        </div>
                      ) : null}
                      {item.fidelidade ? (
                        <pre className="ispjp-pre" style={{ marginTop: '0.35rem' }}>
                          {item.fidelidade}
                        </pre>
                      ) : null}
                    </td>
                    <td>{item.nivel ?? '—'}</td>
                    <td>
                      {item.precoItem || item.precoBase || '—'}
                      {item.tipoPreco ? (
                        <div style={{ color: 'var(--texto-secundario)', fontSize: '0.8rem' }}>
                          {item.tipoPreco}
                        </div>
                      ) : null}
                    </td>
                    <td>{item.promocao || '—'}</td>
                    <td>{item.status || '—'}</td>
                    <td>{item.tecnologiaAcesso || item.tecnologiaVoz || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {atividades.length > 0 && (
        <>
          <div className="ispjp-section-title">Atividades ({atividades.length})</div>
          <div className="ispjp-table-wrap">
            <table className="ispjp-table">
              <thead>
                <tr>
                  <th>Atividade</th>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th>Período</th>
                  <th>Endereço</th>
                  <th>Referência</th>
                </tr>
              </thead>
              <tbody>
                {atividades.map((a, i) => (
                  <tr key={i}>
                    <td>{a.atividade || '—'}</td>
                    <td>{a.tipo || '—'}</td>
                    <td>{a.status || '—'}</td>
                    <td>{a.dataPeriodo || a.dataPeriodoCf || '—'}</td>
                    <td>{a.endereco || '—'}</td>
                    <td>{a.referencia || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

const InfoSpyPj = () => {
  const [aba, setAba] = useState('cnpj');

  // --- Consulta CNPJ (SmartConsultas) ---
  const [scCnpj, setScCnpj] = useState('');
  const [scOrdem, setScOrdem] = useState('');
  const [scLoading, setScLoading] = useState(false);
  const [scError, setScError] = useState('');
  const [consulta, setConsulta] = useState(null);
  const [robotOnline, setRobotOnline] = useState(null);
  const pollRef = useRef(null);

  // --- Sócios (CNPJA) ---
  const [cnpjInput, setCnpjInput] = useState('');
  const [loadingEmpresa, setLoadingEmpresa] = useState(false);
  const [erroEmpresa, setErroEmpresa] = useState('');
  const [empresa, setEmpresa] = useState(null);
  const [socioSelecionado, setSocioSelecionado] = useState(null);
  const [loadingSocio, setLoadingSocio] = useState(false);
  const [erroSocio, setErroSocio] = useState('');
  const [matchSocio, setMatchSocio] = useState(null);
  const [candidatos, setCandidatos] = useState([]);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [erroDetalhe, setErroDetalhe] = useState('');
  const [detalheCpf, setDetalheCpf] = useState(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const aplicarFilaZerada = useCallback(
    (motivo) => {
      stopPolling();
      setConsulta((prev) => {
        if (!prev || !['pending', 'processing'].includes(prev.status)) {
          return prev;
        }
        setScError(
          'Robô offline — todas as consultas em andamento foram canceladas. Você já pode consultar de novo quando o robô voltar.'
        );
        return {
          ...prev,
          status: 'error',
          posicao_fila: null,
          passo: null,
          error_message: motivo
            ? `Fila zerada: robô offline (${motivo})`
            : 'Fila zerada: robô offline'
        };
      });
    },
    [stopPolling]
  );

  const startPolling = useCallback(
    (id) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          // Checa robô: se offline, backend zera a fila e liberamos o usuário
          try {
            const onlineData = await infoSpyPjService.getOnline();
            setRobotOnline(onlineData);
            if (!onlineData?.online) {
              aplicarFilaZerada(onlineData?.motivo);
              // Recarrega status local (já cancelado)
              const cancelled = await infoSpyPjService.getStatus(id);
              if (cancelled.success && cancelled.data) setConsulta(cancelled.data);
              return;
            }
          } catch (onlineErr) {
            const d = onlineErr.response?.data;
            setRobotOnline({ online: false, motivo: d?.motivo || 'Indisponível' });
            aplicarFilaZerada(d?.motivo || 'Indisponível');
            return;
          }

          const response = await infoSpyPjService.getStatus(id);
          if (response.success && response.data) {
            setConsulta(response.data);
            if (['completed', 'error'].includes(response.data.status)) {
              stopPolling();
            }
          }
        } catch (err) {
          console.error('[InfoSpyPj] polling:', err);
        }
      }, POLL_MS);
    },
    [stopPolling, aplicarFilaZerada]
  );

  const refreshOnline = useCallback(async () => {
    try {
      const data = await infoSpyPjService.getOnline();
      setRobotOnline(data);
      if (!data?.online) {
        aplicarFilaZerada(data?.motivo);
      }
      return data;
    } catch (err) {
      const d = err.response?.data;
      setRobotOnline({ online: false, motivo: d?.motivo || 'Indisponível' });
      aplicarFilaZerada(d?.motivo || 'Indisponível');
      return { online: false };
    }
  }, [aplicarFilaZerada]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      await refreshOnline();
      if (cancelled) return;
      try {
        const response = await infoSpyPjService.getConsultaAtiva();
        if (cancelled) return;
        if (response.success && response.data) {
          setConsulta(response.data);
          if (['pending', 'processing'].includes(response.data.status)) {
            startPolling(response.data.id);
          }
        }
      } catch (e) {
        console.error(e);
      }
    })();

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [refreshOnline, startPolling, stopPolling]);

  const consultaEmAndamento =
    consulta && ['pending', 'processing'].includes(consulta.status);

  const handleConsultarSmart = async (e) => {
    e.preventDefault();
    const digits = scCnpj.replace(/\D/g, '');
    const ordem = extrairOrdemSiebel(scOrdem);

    if (digits.length !== 14 && digits.length !== 11) {
      setScError('Informe CNPJ (14) ou CPF (11) válido.');
      return;
    }
    if (!ordem) {
      setScError('Número da ordem inválido. Use o padrão 8-A8639A70FC6-1.');
      return;
    }
    if (ordem !== scOrdem) setScOrdem(ordem);

    // Mesma regra do InfoSpy: 1 consulta por vez
    if (consultaEmAndamento) {
      setScError('Você já possui uma consulta em andamento. Aguarde concluir ou o robô ficar offline.');
      return;
    }

    setScLoading(true);
    setScError('');
    stopPolling();

    try {
      const online = await refreshOnline();
      if (!online?.online) {
        setScLoading(false);
        return;
      }
      const response = await infoSpyPjService.consultar(digits, ordem);
      if (response.success && response.data) {
        setConsulta(response.data);
        if (['pending', 'processing'].includes(response.data.status)) {
          startPolling(response.data.id);
        }
      }
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      if (status === 429 && data?.consulta) {
        setScError(data.error || 'Você já possui uma consulta em andamento');
        setConsulta(data.consulta);
        if (['pending', 'processing'].includes(data.consulta.status)) {
          startPolling(data.consulta.id);
        }
      } else if (status === 503) {
        setScError(data?.error || data?.motivo || 'SmartConsultas offline — fila zerada');
        setRobotOnline({ online: false, motivo: data?.motivo });
        if (data?.filaZerada) aplicarFilaZerada(data?.motivo);
      } else {
        setScError(data?.error || data?.message || 'Erro ao iniciar consulta');
      }
    } finally {
      setScLoading(false);
    }
  };

  const resetSocioState = () => {
    setSocioSelecionado(null);
    setLoadingSocio(false);
    setErroSocio('');
    setMatchSocio(null);
    setCandidatos([]);
    setLoadingDetalhe(false);
    setErroDetalhe('');
    setDetalheCpf(null);
  };

  const handleConsultarCnpja = async (e) => {
    e.preventDefault();
    const digits = cnpjInput.replace(/\D/g, '');
    if (digits.length !== 14) {
      setErroEmpresa('Informe um CNPJ válido com 14 dígitos.');
      setEmpresa(null);
      resetSocioState();
      return;
    }

    setLoadingEmpresa(true);
    setErroEmpresa('');
    setEmpresa(null);
    resetSocioState();

    try {
      const res = await fetch(`https://open.cnpja.com/office/${digits}`);
      if (res.status === 429) {
        setErroEmpresa('Limite de consultas da CNPJA atingido (4/min). Aguarde e tente novamente.');
        return;
      }
      if (res.status === 404) {
        setErroEmpresa('CNPJ não encontrado.');
        return;
      }
      if (!res.ok) {
        setErroEmpresa(`Erro ao consultar CNPJ (HTTP ${res.status}).`);
        return;
      }
      setEmpresa(await res.json());
    } catch (err) {
      setErroEmpresa(
        err.name === 'TypeError'
          ? 'Não foi possível consultar a CNPJA (rede/CORS).'
          : err.message || 'Erro ao consultar CNPJ.'
      );
    } finally {
      setLoadingEmpresa(false);
    }
  };

  const handleClickSocio = async (member) => {
    const person = member?.person || {};
    setSocioSelecionado(member);
    setMatchSocio(null);
    setCandidatos([]);
    setDetalheCpf(null);
    setErroDetalhe('');
    setErroSocio('');
    setLoadingSocio(true);

    try {
      const data = await infoSpyPjService.buscarPorNome(person.name || '', person.taxId || '');
      if (data.match) {
        setMatchSocio(data.match);
        setCandidatos(data.candidatos?.length > 1 ? data.candidatos : []);
      } else if (data.candidatos?.length) {
        setCandidatos(data.candidatos);
        setErroSocio('Não foi possível casar o CPF pelo meio mascarado. Selecione um candidato.');
      } else {
        setErroSocio('Nenhum resultado encontrado para este sócio.');
      }
    } catch (err) {
      setErroSocio(err.response?.data?.error || err.message || 'Erro ao buscar sócio.');
    } finally {
      setLoadingSocio(false);
    }
  };

  const handleConsultarDetalhe = async (cpf) => {
    const digits = String(cpf || '').replace(/\D/g, '');
    if (digits.length !== 11) {
      setErroDetalhe('CPF inválido.');
      return;
    }
    setLoadingDetalhe(true);
    setErroDetalhe('');
    setDetalheCpf(null);
    try {
      setDetalheCpf(await infoSpyPjService.buscarCpf(digits));
    } catch (err) {
      setErroDetalhe(err.response?.data?.error || err.message || 'Erro ao consultar CPF.');
    } finally {
      setLoadingDetalhe(false);
    }
  };

  const members = empresa?.company?.members || [];
  const phones = empresa?.phones || [];
  const emails = empresa?.emails || [];
  const sideActivities = empresa?.sideActivities || [];

  const detalheCampos = detalheCpf
    ? [
        { label: 'CPF', valor: formatCpfDisplay(detalheCpf.CPF) },
        { label: 'Nome', valor: detalheCpf.NOME },
        { label: 'Nascimento', valor: detalheCpf.DT_NASCIMENTO },
        { label: 'Sexo', valor: detalheCpf.SEXO },
        { label: 'Nome da mãe', valor: detalheCpf.NOME_MAE },
        { label: 'Status RF', valor: detalheCpf.STATUS_RECEITA_FEDERAL },
        { label: 'E-mail', valor: detalheCpf.EMAIL },
        { label: 'Telefones', valor: formatPhone(detalheCpf.TELEFONES) },
        {
          label: 'Endereço',
          valor: [
            [detalheCpf.TIPO_ENDERECO, detalheCpf.LOGRADOURO].filter(Boolean).join(' '),
            detalheCpf.NUMERO && detalheCpf.NUMERO !== '0' ? `nº ${detalheCpf.NUMERO}` : null,
            !isEmptyResult(detalheCpf.COMPLEMENTO) ? detalheCpf.COMPLEMENTO : null,
            detalheCpf.BAIRRO,
            [detalheCpf.CIDADE, detalheCpf.UF].filter(Boolean).join(' - '),
            detalheCpf.CEP ? `CEP ${formatCep(detalheCpf.CEP)}` : null
          ]
            .filter(Boolean)
            .join(', ')
        },
        { label: 'Renda presumida', valor: formatMoney(detalheCpf.RENDA_PRESUMIDA) },
        { label: 'Faixa de renda', valor: detalheCpf.FAIXA_RENDA },
        { label: 'CBO', valor: detalheCpf.CBO },
        {
          label: '% cargo societário',
          valor: detalheCpf.PCT_CARGO_SOCIETARIO ? `${detalheCpf.PCT_CARGO_SOCIETARIO}%` : null
        },
        { label: 'Qtd. veículos', valor: detalheCpf.QT_VEICULOS },
        {
          label: 'Óbito',
          valor:
            detalheCpf.FLAG_OBITO === '1'
              ? `Sim${
                  detalheCpf.DT_OBITO && !isEmptyResult(detalheCpf.DT_OBITO)
                    ? ` (${detalheCpf.DT_OBITO})`
                    : ''
                }`
              : 'Não'
        }
      ].filter((c) => !isEmptyResult(c.valor))
    : [];

  const st = consulta ? statusConfig(consulta.status) : null;

  return (
    <Layout>
      <div className="ispjp-container">
        <div className="ispjp-header">
          <div>
            <h1 className="ispjp-title">
              <div className="ispjp-title-icon">◈</div>
              InfoSpyPj
            </h1>
            <p className="ispjp-subtitle">
              Consulta de ordem no Siebel (SmartConsultas) e identificação de sócios
            </p>
          </div>
          <div>
            <Link to="/infospypj/historico" className="ispjp-btn ispjp-btn-secondary">
              Histórico
            </Link>
          </div>
        </div>

        <div className="ispjp-tabs" role="tablist">
          <button
            type="button"
            className={`ispjp-tab ${aba === 'cnpj' ? 'active' : ''}`}
            onClick={() => setAba('cnpj')}
          >
            Consulta CNPJ
          </button>
          <button
            type="button"
            className={`ispjp-tab ${aba === 'socios' ? 'active' : ''}`}
            onClick={() => setAba('socios')}
          >
            Consulta Sócios
          </button>
        </div>

        {aba === 'cnpj' && (
          <>
            <div className="ispjp-card">
              <div className="ispjp-card-header">
                <div className="ispjp-card-header-left">
                  <span className="ispjp-card-header-title">Nova consulta</span>
                </div>
                <div className="ispjp-robot">
                  <span
                    className={`ispjp-robot-dot ${robotOnline?.online ? 'on' : 'off'}`}
                  />
                  {robotOnline?.online
                    ? `Robô online${robotOnline.naFila != null ? ` · fila ${robotOnline.naFila}` : ''}`
                    : `Robô offline${robotOnline?.motivo ? `: ${robotOnline.motivo}` : ''}`}
                  <button
                    type="button"
                    className="ispjp-btn ispjp-btn-secondary"
                    style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
                    onClick={refreshOnline}
                  >
                    Atualizar
                  </button>
                </div>
              </div>
              <div className="ispjp-card-body">
                <form className="ispjp-form" onSubmit={handleConsultarSmart}>
                  <div className="ispjp-form-group">
                    <label className="ispjp-form-label" htmlFor="ispjp-sc-cnpj">
                      CNPJ / CPF
                    </label>
                    <input
                      id="ispjp-sc-cnpj"
                      type="text"
                      className="ispjp-form-input"
                      placeholder="00.000.000/0000-00"
                      value={scCnpj}
                      onChange={(e) => setScCnpj(formatCnpjInput(e.target.value))}
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </div>
                  <div className="ispjp-form-group">
                    <label className="ispjp-form-label" htmlFor="ispjp-sc-ordem">
                      Número da ordem
                    </label>
                    <input
                      id="ispjp-sc-ordem"
                      type="text"
                      className="ispjp-form-input"
                      placeholder="8-A8639A70FC6-1"
                      value={scOrdem}
                      onChange={(e) => setScOrdem(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <button
                    type="submit"
                    className="ispjp-btn ispjp-btn-primary"
                    disabled={
                      scLoading ||
                      robotOnline?.online === false ||
                      consultaEmAndamento
                    }
                  >
                    {scLoading
                      ? 'Enfileirando…'
                      : consultaEmAndamento
                        ? 'Consulta em andamento…'
                        : 'Consultar'}
                  </button>
                </form>
              </div>
            </div>

            {scError && <div className="ispjp-error">{scError}</div>}

            {consulta && (
              <div className="ispjp-card">
                <div className="ispjp-card-header">
                  <div className="ispjp-card-header-left">
                    <span className="ispjp-card-header-title">
                      {formatCnpjDisplay(consulta.cnpj)} · {consulta.ordem}
                    </span>
                  </div>
                </div>
                <div className="ispjp-card-body">
                  <div className="ispjp-status-bar">
                    <span className={`ispjp-status-pill ${st.class}`}>{st.label}</span>
                    {['pending', 'processing'].includes(consulta.status) && (
                      <>
                        <span className="ispjp-status-pill">
                          Posição: {consulta.posicao_fila != null ? `#${consulta.posicao_fila}` : '…'}
                        </span>
                        {consulta.passo && (
                          <span className="ispjp-status-pill">Passo: {consulta.passo}</span>
                        )}
                        <div className="ispjp-loading">
                          <div className="ispjp-spinner" />
                          Aguardando robô (1–3 min)…
                        </div>
                      </>
                    )}
                  </div>

                  {consulta.status === 'error' && (
                    <div className="ispjp-error">
                      {consulta.error_message || 'Erro na consulta'}
                      {consulta.passo ? ` · passo: ${consulta.passo}` : ''}
                    </div>
                  )}

                  {consulta.resultado && (
                    <ResultadoSmartConsultas
                      resultado={consulta.resultado}
                      consulta={consulta}
                    />
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {aba === 'socios' && (
          <>
            <div className="ispjp-card">
              <div className="ispjp-card-header">
                <div className="ispjp-card-header-left">
                  <span className="ispjp-card-header-title">Buscar empresa</span>
                </div>
              </div>
              <div className="ispjp-card-body">
                <form className="ispjp-form" onSubmit={handleConsultarCnpja}>
                  <div className="ispjp-form-group">
                    <label className="ispjp-form-label" htmlFor="ispjp-cnpj">
                      CNPJ do cliente
                    </label>
                    <input
                      id="ispjp-cnpj"
                      type="text"
                      className="ispjp-form-input"
                      placeholder="00.000.000/0000-00"
                      value={cnpjInput}
                      onChange={(e) => setCnpjInput(formatCnpjInput(e.target.value))}
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </div>
                  <button
                    type="submit"
                    className="ispjp-btn ispjp-btn-primary"
                    disabled={loadingEmpresa}
                  >
                    {loadingEmpresa ? 'Consultando…' : 'Consultar'}
                  </button>
                </form>
              </div>
            </div>

            {erroEmpresa && <div className="ispjp-error">{erroEmpresa}</div>}

            {loadingEmpresa && (
              <div className="ispjp-card">
                <div className="ispjp-card-body">
                  <div className="ispjp-loading">
                    <div className="ispjp-spinner" />
                    Consultando CNPJA…
                  </div>
                </div>
              </div>
            )}

            {empresa && !loadingEmpresa && (
              <>
                <div className="ispjp-card">
                  <div className="ispjp-card-header">
                    <div className="ispjp-card-header-left">
                      <span className="ispjp-card-header-title">
                        {empresa.company?.name || empresa.alias || 'Empresa'}
                      </span>
                      {empresa.status?.text && (
                        <span
                          className={`ispjp-badge ${
                            String(empresa.status.text).toLowerCase() === 'ativa'
                              ? 'ispjp-badge-ok'
                              : ''
                          }`}
                        >
                          {empresa.status.text}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ispjp-card-body">
                    <div className="ispjp-section-title">Identificação</div>
                    <div className="ispjp-field-grid">
                      <Campo label="CNPJ" valor={formatCnpjDisplay(empresa.taxId)} />
                      <Campo label="Nome fantasia" valor={empresa.alias} />
                      <Campo label="Razão social" valor={empresa.company?.name} />
                      <Campo label="Fundada em" valor={formatDateBr(empresa.founded)} />
                      <Campo label="Status desde" valor={formatDateBr(empresa.statusDate)} />
                      <Campo label="Matriz" valor={empresa.head ? 'Sim' : 'Não'} />
                      <Campo label="Natureza" valor={empresa.company?.nature?.text} />
                      <Campo
                        label="Porte"
                        valor={
                          empresa.company?.size
                            ? `${empresa.company.size.acronym || ''} — ${empresa.company.size.text || ''}`.trim()
                            : null
                        }
                      />
                      <Campo label="Capital social" valor={formatMoney(empresa.company?.equity)} />
                      <Campo
                        label="Simples"
                        valor={
                          empresa.company?.simples?.optant
                            ? `Optante${
                                empresa.company.simples.since
                                  ? ` desde ${formatDateBr(empresa.company.simples.since)}`
                                  : ''
                              }`
                            : 'Não optante'
                        }
                      />
                      <Campo
                        label="Simei"
                        valor={
                          empresa.company?.simei?.optant
                            ? `Optante${
                                empresa.company.simei.since
                                  ? ` desde ${formatDateBr(empresa.company.simei.since)}`
                                  : ''
                              }`
                            : 'Não optante'
                        }
                      />
                    </div>

                    <div className="ispjp-section-title">Endereço e contatos</div>
                    <div className="ispjp-field-grid">
                      <Campo label="Endereço" valor={buildAddress(empresa.address)} />
                      <Campo
                        label="Telefone(s)"
                        valor={phones.length ? phones.map((p) => formatPhone(p)).join(' · ') : null}
                      />
                      <Campo
                        label="E-mail(s)"
                        valor={
                          emails.length
                            ? emails.map((em) => em.address).filter(Boolean).join(' · ')
                            : null
                        }
                      />
                    </div>

                    <div className="ispjp-section-title">Atividades</div>
                    <div className="ispjp-field-grid">
                      <Campo
                        label="CNAE principal"
                        valor={
                          empresa.mainActivity
                            ? `${empresa.mainActivity.id} — ${empresa.mainActivity.text}`
                            : null
                        }
                      />
                    </div>
                    {sideActivities.length > 0 && (
                      <div className="ispjp-side-activities" style={{ marginTop: '0.75rem' }}>
                        {sideActivities.map((a) => (
                          <div key={a.id} className="ispjp-side-activity">
                            {a.id} — {a.text}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="ispjp-card">
                  <div className="ispjp-card-header">
                    <div className="ispjp-card-header-left">
                      <span className="ispjp-card-header-title">Sócios ({members.length})</span>
                    </div>
                  </div>
                  <div className="ispjp-card-body">
                    {members.length === 0 ? (
                      <p style={{ color: 'var(--texto-secundario)', margin: 0 }}>
                        Nenhum sócio informado nesta consulta.
                      </p>
                    ) : (
                      <>
                        <p className="ispjp-info" style={{ marginTop: 0 }}>
                          Clique em um sócio para resolver o CPF completo.
                        </p>
                        <ul className="ispjp-list">
                          {members.map((m) => {
                            const person = m.person || {};
                            const key = person.id || `${person.name}-${person.taxId}`;
                            const active =
                              socioSelecionado?.person?.id === person.id ||
                              (socioSelecionado?.person?.name === person.name &&
                                socioSelecionado?.person?.taxId === person.taxId);
                            return (
                              <li key={key}>
                                <button
                                  type="button"
                                  className={`ispjp-socio ${active ? 'active' : ''}`}
                                  onClick={() => handleClickSocio(m)}
                                  disabled={loadingSocio}
                                >
                                  <div className="ispjp-socio-main">
                                    <span className="ispjp-socio-nome">{person.name || '—'}</span>
                                    <span className="ispjp-socio-meta">
                                      {m.role?.text || 'Sócio'}
                                      {m.since ? ` · desde ${formatDateBr(m.since)}` : ''}
                                      {person.age ? ` · faixa ${person.age}` : ''}
                                    </span>
                                  </div>
                                  <div className="ispjp-socio-right">
                                    <span className="ispjp-socio-cpf">{person.taxId || '—'}</span>
                                    <span className="ispjp-socio-meta">Ver CPF</span>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {(loadingSocio || socioSelecionado) && (
              <div className="ispjp-card">
                <div className="ispjp-card-header">
                  <div className="ispjp-card-header-left">
                    <span className="ispjp-card-header-title">
                      Sócio: {socioSelecionado?.person?.name || '—'}
                    </span>
                  </div>
                </div>
                <div className="ispjp-card-body">
                  {loadingSocio && (
                    <div className="ispjp-loading">
                      <div className="ispjp-spinner" />
                      Buscando CPF completo…
                    </div>
                  )}
                  {erroSocio && <div className="ispjp-error">{erroSocio}</div>}
                  {!loadingSocio && matchSocio && (
                    <>
                      <div className="ispjp-field-grid">
                        <Campo label="Nome" valor={matchSocio.NOME} />
                        <Campo label="CPF" valor={formatCpfDisplay(matchSocio.CPF)} />
                        <Campo label="Nascimento" valor={matchSocio.NASCIMENTO} />
                        <Campo label="Sexo" valor={matchSocio.SEXO} />
                      </div>
                      <div className="ispjp-actions">
                        <button
                          type="button"
                          className="ispjp-btn ispjp-btn-primary"
                          onClick={() => handleConsultarDetalhe(matchSocio.CPF)}
                          disabled={loadingDetalhe}
                        >
                          {loadingDetalhe ? 'Consultando…' : 'Consultar detalhes do CPF'}
                        </button>
                      </div>
                    </>
                  )}
                  {!loadingSocio && !matchSocio && candidatos.length > 0 && (
                    <>
                      <div className="ispjp-section-title">Candidatos encontrados</div>
                      <ul className="ispjp-list">
                        {candidatos.map((c) => (
                          <li key={c.CPF}>
                            <button
                              type="button"
                              className="ispjp-socio"
                              onClick={() => {
                                setMatchSocio(c);
                                setErroSocio('');
                                setDetalheCpf(null);
                              }}
                            >
                              <div className="ispjp-socio-main">
                                <span className="ispjp-socio-nome">{c.NOME}</span>
                                <span className="ispjp-socio-meta">
                                  Nasc. {c.NASCIMENTO || '—'} · Sexo {c.SEXO || '—'}
                                </span>
                              </div>
                              <div className="ispjp-socio-right">
                                <span className="ispjp-socio-cpf">{formatCpfDisplay(c.CPF)}</span>
                                <span className="ispjp-socio-meta">Selecionar</span>
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            )}

            {(loadingDetalhe || detalheCpf || erroDetalhe) && (
              <div className="ispjp-card">
                <div className="ispjp-card-header">
                  <div className="ispjp-card-header-left">
                    <span className="ispjp-card-header-title">Detalhes do CPF</span>
                  </div>
                </div>
                <div className="ispjp-card-body">
                  {loadingDetalhe && (
                    <div className="ispjp-loading">
                      <div className="ispjp-spinner" />
                      Consultando ficha cadastral…
                    </div>
                  )}
                  {erroDetalhe && <div className="ispjp-error">{erroDetalhe}</div>}
                  {!loadingDetalhe && detalheCpf && (
                    <div className="ispjp-field-grid">
                      {detalheCampos.map((c) => (
                        <Campo key={c.label} label={c.label} valor={c.valor} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default InfoSpyPj;
